import React from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema } from '../utils/structuredData';
import { APPS_SUPPORT } from '../utils/appsData';
import { LifeBuoyIcon, ArrowRightIcon, MailIcon, MessageSquareIcon } from 'lucide-react';
export function SupportPage() {
  usePageSEO({
    title: 'Support Center | Design Flow Digitals',
    description: 'Get help, documentation, and support for Design Flow Digitals Shopify apps including SEO Suite, AI Photoshoot, and FlowBoost.',
    canonical: '/support',
    keywords: 'Shopify app support, Design Flow Digitals support, help center, app documentation',
    jsonLd: breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Support',
      path: '/support'
    }])
  });
  return <div className="w-full pt-32 pb-20" data-id="element-1630" data-mp="3jiexvlzdjfck">
      {/* Hero */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto text-center mb-20" data-id="element-1631" data-mp="3eklq4oifada6">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-white/10" data-id="element-1632" data-mp="34y51lkx53hxg">
          <LifeBuoyIcon className="w-4 h-4 text-gray-400" data-id="element-1633" data-mp="39veootx23hi4" />
          <span className="text-sm font-medium text-gray-300" data-id="element-1634" data-mp="3en50737qcbb4">
            Support Center
          </span>
        </div>
        <h1 className="text-5xl md:text-7xl font-bold mb-6" data-id="element-1635" data-mp="3ip9snymfz1w6">
          How can we help you?
        </h1>
        <p className="text-xl md:text-2xl text-gray-400 leading-relaxed max-w-3xl mx-auto" data-id="element-1636" data-mp="2lz98ye0xjblq">
          Select an app below to view setup guides, troubleshooting steps, and
          documentation.
        </p>
      </section>

      {/* Quick Contact */}
      <section className="px-6 md:px-12 max-w-3xl mx-auto mb-20" data-id="element-1637" data-mp="2v1a5f8oc9tmc">
        <div className="glass-card p-6 md:p-8 rounded-3xl border-white/10 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left" data-id="element-1638" data-mp="2zz1ttspryxls">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6" data-id="element-1639" data-mp="f7rpw5lj0l2m">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/5 flex items-center justify-center shrink-0" data-id="element-1640" data-mp="2bb65d3a4eyfp">
              <MailIcon className="w-6 h-6 sm:w-8 sm:h-8 text-white" data-id="element-1641" data-mp="27a43sfsqeaak" />
            </div>
            <div data-id="element-1642" data-mp="2l93qhhwoof43">
              <h3 className="text-lg sm:text-xl font-bold mb-1" data-id="element-1643" data-mp="2l9lytcwmejsd">
                Direct Support
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-1644" data-mp="2gbke6njjqces">
                Email us at{' '}
                <a href="mailto:support@designflowdigitals.com" className="text-white hover:underline" data-id="element-1645" data-mp="1sjwr6gpenlhs">
                  support@designflowdigitals.com
                </a>
              </p>
              <p className="text-gray-500 text-xs mt-1" data-id="element-1646" data-mp="22ckp5es4fvi3">
                We typically reply within 24 hours.
              </p>
            </div>
          </div>
          <Link to="/contact" className="w-full sm:w-auto shrink-0 inline-flex justify-center items-center gap-2 bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors" data-id="element-1647" data-mp="19t5dj2ydx8xl">
            Open a Ticket
          </Link>
        </div>
      </section>

      {/* App Grid */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto" data-id="element-1648" data-mp="3lb8v4aoxck1q">
        <h2 className="text-2xl font-bold mb-8 text-center" data-id="element-1649" data-mp="37isoz10q85wl">
          App Documentation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-1650" data-mp="3bke7p2zxzeep">
          {APPS_SUPPORT.map(app => {
          const Icon = app.icon;
          return <Link key={app.slug} to={`/support/${app.slug}`} className="glass-card p-8 rounded-3xl border-white/5 hover:border-white/20 transition-all duration-300 group hover:-translate-y-1" data-id="element-1651" data-mp="82dw7dv6gqwc">
                <div className={`w-14 h-14 rounded-2xl mb-6 flex items-center justify-center ${app.accentBg} border ${app.accentBorder} group-hover:scale-110 transition-transform duration-300`} data-id="element-1652" data-mp="3v5inhsdv9icg">
                  <Icon className={`w-7 h-7 ${app.accentColor}`} data-id="element-1653" data-mp="2z1tnbpagskmb" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-400 transition-all" data-id="element-1654" data-mp="2z3oazbd4equ3">
                  {app.name}
                </h3>
                <p className="text-gray-400 mb-8 line-clamp-2" data-id="element-1655" data-mp="2lbq65cqfzh3q">
                  {app.description}
                </p>
                <div className={`inline-flex items-center gap-2 text-sm font-medium ${app.accentColor}`} data-id="element-1656" data-mp="3hzwh0hdyj5qg">
                  View Documentation{' '}
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1657" data-mp="38yg7ebuyv7na" />
                </div>
              </Link>;
        })}
        </div>
      </section>

      {/* General FAQ / Bottom CTA */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto mt-32 text-center" data-id="element-1658" data-mp="1xihw01cbg9iw">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6" data-id="element-1659" data-mp="1skgdpimpse8h">
          <MessageSquareIcon className="w-8 h-8 text-gray-400" data-id="element-1660" data-mp="81mbg5zu1tr6" />
        </div>
        <h2 className="text-3xl font-bold mb-4" data-id="element-1661" data-mp="d3bkjg3hvxcm">Have a general inquiry?</h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto" data-id="element-1662" data-mp="h3cbg07s610k">
          Whether you have a question about billing, custom development, or
          partnership opportunities, our team is ready to assist.
        </p>
        <Link to="/contact" className="inline-flex items-center gap-2 bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-1663" data-mp="qtf5m3qn1l0h">
          Contact Us <ArrowRightIcon className="w-5 h-5" data-id="element-1664" data-mp="10q9zuafvoz8y" />
        </Link>
      </section>
    </div>;
}